import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Plus, X, AlertTriangle, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import { OBRAS, fmt } from './insumos.utils';

function TransferenciaModal({ onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ material_id: '', obra_origem: '', obra_destino: '', quantidade: '', responsavel: '', observacoes: '' });
  const [saldoOrigem, setSaldoOrigem] = useState(null);
  const [erro, setErro] = useState('');

  const { data: materiais } = useQuery({ queryKey: ['insumos-materiais-all'], queryFn: () => api.get('/insumos/materiais', { params: { limit: 500 } }).then(r => r.data.data) });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function checkSaldo(materialId, obra) {
    if (!materialId || !obra) return;
    try {
      const { data } = await api.get('/insumos/saldo', { params: { obra } });
      const s = data.data.find(x => x.material_id === parseInt(materialId));
      setSaldoOrigem(s ? parseFloat(s.saldo) : 0);
    } catch { setSaldoOrigem(null); }
  }

  const save = useMutation({
    mutationFn: () => api.post('/insumos/transferencias', form),
    onSuccess: () => { qc.invalidateQueries(['insumos-transferencias']); qc.invalidateQueries(['insumos-saldo']); onClose(); },
    onError: e => setErro(e.response?.data?.error || 'Erro ao transferir'),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-800 text-lg">Nova Transferência</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        {erro && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex gap-2"><AlertTriangle size={16} />{erro}</div>}

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Material *</label>
            <select value={form.material_id} onChange={e => { set('material_id', e.target.value); checkSaldo(e.target.value, form.obra_origem); }}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecionar...</option>
              {(materiais || []).map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Obra Origem *</label>
              <select value={form.obra_origem} onChange={e => { set('obra_origem', e.target.value); checkSaldo(form.material_id, e.target.value); }}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="">Selecionar...</option>
                {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Obra Destino *</label>
              <select value={form.obra_destino} onChange={e => set('obra_destino', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="">Selecionar...</option>
                {OBRAS.filter(o => o !== form.obra_origem).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Quantidade * {saldoOrigem !== null && <span className="text-blue-500">(disponível: {fmt(saldoOrigem)})</span>}
            </label>
            <input type="number" min="0.01" step="0.01" value={form.quantidade} onChange={e => set('quantidade', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Responsável</label>
            <input value={form.responsavel} onChange={e => set('responsavel', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Observações</label>
            <input value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.material_id || !form.obra_origem || !form.obra_destino || !form.quantidade}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {save.isPending ? 'Transferindo...' : 'Transferir'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InsumosTransferencias() {
  const [modal, setModal] = useState(false);
  const [obraFiltro, setObraFiltro] = useState('');

  const { data } = useQuery({
    queryKey: ['insumos-transferencias', obraFiltro],
    queryFn: () => api.get('/insumos/transferencias', { params: { obra_origem: obraFiltro || undefined } }).then(r => r.data.data),
  });

  const rows = data || [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ArrowLeftRight size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-800">Transferências entre Obras</h1>
        </div>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
          <Plus size={16} /> Nova Transferência
        </button>
      </div>

      <select value={obraFiltro} onChange={e => setObraFiltro(e.target.value)}
        className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
        <option value="">Todas as obras</option>
        {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 border-b">
            <tr>
              <th className="px-4 py-3 text-left">Material</th>
              <th className="px-4 py-3 text-left">Origem</th>
              <th className="px-4 py-3 text-center">→</th>
              <th className="px-4 py-3 text-left">Destino</th>
              <th className="px-4 py-3 text-right">Quantidade</th>
              <th className="px-4 py-3 text-left">Responsável</th>
              <th className="px-4 py-3 text-left">Data</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map(r => (
              <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-800">{r.material_nome}</p>
                  <p className="text-xs text-gray-400 font-mono">{r.codigo}</p>
                </td>
                <td className="px-4 py-3 text-gray-600">{r.obra_origem}</td>
                <td className="px-4 py-3 text-center text-gray-400"><ArrowLeftRight size={14} /></td>
                <td className="px-4 py-3 text-gray-600">{r.obra_destino}</td>
                <td className="px-4 py-3 text-right font-mono">{fmt(r.quantidade)} {r.unidade_sigla || ''}</td>
                <td className="px-4 py-3 text-gray-500">{r.responsavel || '—'}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{format(parseISO(r.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}</td>
              </tr>
            )) : (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">Nenhuma transferência registrada</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && <TransferenciaModal onClose={() => setModal(false)} />}
    </div>
  );
}
